/**
 * Reusable Pagination Hook
 * 
 * Manages pagination state with URL synchronization and search debouncing.
 * Can be used across all entity pages (Vendors, Invoices, PriceBooks, etc.)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_SEARCH_DEBOUNCE_MS = 300;
const STORAGE_PREFIX = 'pagination_';

// localStorage helpers with error handling for private browsing mode
const getStorageKey = (storageKey: string, param: string) => 
  `${STORAGE_PREFIX}${storageKey}_${param}`;

const getFromStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setToStorage = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

export interface UsePaginationOptions {
  /** Default page size (default: 25) */
  defaultPageSize?: number;
  /** Available page size options (default: [10, 25, 50, 100]) */
  pageSizeOptions?: number[];
  /** Debounce delay for search input in ms (default: 300) */
  searchDebounceMs?: number;
  /** Whether to persist state to URL (default: true) */
  persistToUrl?: boolean;
  /** Optional prefix for URL params (e.g., "vendors_") */
  urlParamPrefix?: string;
  /** Storage key for localStorage persistence (e.g., "vendors", "invoices") */
  storageKey?: string;
}

export interface UsePaginationReturn {
  // State
  pageIndex: number;
  pageSize: number;
  searchValue: string;
  debouncedSearch: string;
  
  // Setters
  setPageIndex: (index: number) => void;
  setPageSize: (size: number) => void;
  setSearchValue: (value: string) => void;
  
  // Computed
  offset: number;
  pageSizeOptions: number[];
  
  // Helpers
  resetToFirstPage: () => void;
  goToNextPage: (totalCount: number) => void;
  goToPreviousPage: () => void;
  getTotalPages: (totalCount: number) => number;
  canGoNext: (totalCount: number) => boolean;
  canGoPrevious: boolean;
  
  // For AGDataGrid integration
  paginationState: {
    pageIndex: number;
    pageSize: number;
  };
}

export function usePagination(options: UsePaginationOptions = {}): UsePaginationReturn {
  const {
    defaultPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
    persistToUrl = true,
    urlParamPrefix = '',
    storageKey,
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();
  
  // Build param names with optional prefix
  const pageParam = `${urlParamPrefix}page`;
  const pageSizeParam = `${urlParamPrefix}pageSize`;
  
  // Initialize state from URL, then localStorage, then defaults
  const getInitialPageIndex = (): number => {
    // First check URL (highest priority)
    if (persistToUrl) {
      const urlPage = searchParams.get(pageParam);
      if (urlPage !== null) {
        const parsed = parseInt(urlPage, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          return parsed;
        }
      }
    }
    
    // Then check localStorage
    if (storageKey) {
      const stored = getFromStorage(getStorageKey(storageKey, 'page'));
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          return parsed;
        }
      }
    }
    
    return 0;
  };
  
  const getInitialPageSize = (): number => {
    // First check URL (highest priority)
    if (persistToUrl) {
      const urlSize = searchParams.get(pageSizeParam);
      if (urlSize !== null) {
        const parsed = parseInt(urlSize, 10);
        if (!isNaN(parsed) && pageSizeOptions.includes(parsed)) {
          return parsed;
        }
      }
    }
    
    // Then check localStorage
    if (storageKey) {
      const stored = getFromStorage(getStorageKey(storageKey, 'pageSize'));
      if (stored !== null) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && pageSizeOptions.includes(parsed)) {
          return parsed;
        }
      }
    }
    
    return defaultPageSize;
  };

  const [pageIndex, setPageIndexState] = useState<number>(getInitialPageIndex);
  const [pageSize, setPageSizeState] = useState<number>(getInitialPageSize);
  const [searchValue, setSearchValue] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  // Debounce search and reset to first page when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
      // Reset to first page when search changes (only if search has content)
      if (searchValue !== debouncedSearch) {
        setPageIndexState(0);
      }
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchValue, searchDebounceMs]);

  // Sync state to URL
  useEffect(() => {
    if (!persistToUrl) return;

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      
      // Only set page if not 0 (to keep URL clean)
      if (pageIndex > 0) {
        newParams.set(pageParam, String(pageIndex));
      } else {
        newParams.delete(pageParam);
      }
      
      // Only set pageSize if different from default
      if (pageSize !== defaultPageSize) {
        newParams.set(pageSizeParam, String(pageSize));
      } else {
        newParams.delete(pageSizeParam);
      }
      
      return newParams;
    }, { replace: true });
  }, [pageIndex, pageSize, persistToUrl, pageParam, pageSizeParam, defaultPageSize, setSearchParams]);

  // Sync state to localStorage
  useEffect(() => {
    if (!storageKey) return;
    
    setToStorage(getStorageKey(storageKey, 'pageSize'), String(pageSize));
    setToStorage(getStorageKey(storageKey, 'page'), String(pageIndex));
  }, [pageIndex, pageSize, storageKey]);

  // Setters with validation
  const setPageIndex = useCallback((index: number) => {
    setPageIndexState(Math.max(0, index));
  }, []);

  const setPageSize = useCallback((size: number) => {
    if (pageSizeOptions.includes(size)) {
      setPageSizeState(size);
      // Reset to first page when page size changes
      setPageIndexState(0);
    }
  }, [pageSizeOptions]);

  // Helper functions
  const resetToFirstPage = useCallback(() => {
    setPageIndexState(0);
  }, []);

  const getTotalPages = useCallback((totalCount: number): number => {
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [pageSize]);

  const canGoNext = useCallback((totalCount: number): boolean => {
    return pageIndex < getTotalPages(totalCount) - 1;
  }, [pageIndex, getTotalPages]);

  const canGoPrevious = pageIndex > 0;

  const goToNextPage = useCallback((totalCount: number) => {
    if (canGoNext(totalCount)) {
      setPageIndexState((prev) => prev + 1);
    }
  }, [canGoNext]);

  const goToPreviousPage = useCallback(() => {
    if (canGoPrevious) {
      setPageIndexState((prev) => prev - 1);
    }
  }, [canGoPrevious]);

  // Computed values
  const offset = useMemo(() => pageIndex * pageSize, [pageIndex, pageSize]);

  const paginationState = useMemo(() => ({
    pageIndex,
    pageSize,
  }), [pageIndex, pageSize]);

  return {
    // State
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    
    // Setters
    setPageIndex,
    setPageSize,
    setSearchValue,
    
    // Computed
    offset,
    pageSizeOptions,
    
    // Helpers
    resetToFirstPage,
    goToNextPage,
    goToPreviousPage,
    getTotalPages,
    canGoNext,
    canGoPrevious,
    
    // For AGDataGrid
    paginationState,
  };
}
